/**
 * Shared author byline pieces for ask-on-behalf questions.
 *
 * QuestionCard (feed) and QuestionDetails rendered identical five-way avatar
 * and four-way name logic inline (~80 lines each) that drifted over time. These
 * two components own that logic once, driven by the `getAskOnBehalfView` result;
 * each surface keeps its own surrounding layout (space/time row, pin badge,
 * subtitle placement) and passes a `variant` for its typography/sizing.
 *
 * Per-claim-state principal:
 *   - claimed  → beneficiary (now the owner)
 *   - declined → original asker (ownership reverted)
 *   - pending  → original asker for the name; stacked avatars for the avatar
 *   - none     → the question's author
 */
import React from 'react';
import { UserAvatar } from '@components/UserAvatar';
import { StackedAvatars } from './StackedAvatars';
import { cn } from '@lib/utils';
import { isPathBuildable } from '@utils/slugHelpers';
import type { AskOnBehalfView } from '@utils/askOnBehalf';
import type { UserSummary } from '@models/api';

export type QuestionBylineVariant = 'card' | 'detail';

interface QuestionAuthorBylineProps {
  view: AskOnBehalfView;
  /** Normal-case author (question.user). */
  author: UserSummary | null;
  isAnonymous: boolean;
  variant: QuestionBylineVariant;
  /** Navigate to the displayed author/creator. The surface owns analytics + returnTo. */
  onAuthorClick: (e: React.MouseEvent) => void;
  /** Navigate to the beneficiary. */
  onBeneficiaryClick: (e: React.MouseEvent) => void;
}

const AVATAR_STYLES = {
  card: {
    size: 'xl' as const,
    anonymous: 'w-12 h-12',
    principal: 'w-[52px] h-[52px] cursor-pointer hover:ring-2 hover:ring-gray-200 transition-all',
    principalStatic: 'w-[52px] h-[52px] transition-all',
  },
  detail: {
    size: 'md' as const,
    anonymous: 'w-10 h-10 ring-2 ring-gray-100',
    principal: 'w-10 h-10 ring-2 ring-gray-100 cursor-pointer',
    principalStatic: 'w-10 h-10 ring-2 ring-gray-100',
  },
};

const PRINCIPAL_BUTTON_CLASS =
  'rounded-full focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all';

const NAME_STYLES = {
  card: {
    base: 'font-medium text-mb-gray-dark text-base',
    extra: 'truncate',
  },
  detail: {
    base: 'font-semibold text-gray-900 text-base',
    extra: '',
  },
};

const NAME_INTERACTIVE = 'cursor-pointer hover:text-gray-600 transition-colors';

/** The single avatar/name subject + its click handler for the current claim state. */
function principalFor(
  view: AskOnBehalfView,
  author: UserSummary | null,
  onAuthorClick: QuestionAuthorBylineProps['onAuthorClick'],
  onBeneficiaryClick: QuestionAuthorBylineProps['onBeneficiaryClick'],
): { user: UserSummary | null; onClick: (e: React.MouseEvent) => void } {
  if (view.kind === 'claimed' && view.beneficiary) {
    return { user: view.beneficiary, onClick: onBeneficiaryClick };
  }
  if (view.kind === 'declined' && view.originalAsker) {
    return { user: view.originalAsker, onClick: onAuthorClick };
  }
  if (view.kind === 'pending' && view.originalAsker) {
    return { user: view.originalAsker, onClick: onAuthorClick };
  }
  return { user: author, onClick: onAuthorClick };
}

export const QuestionAuthorAvatar: React.FC<QuestionAuthorBylineProps> = ({
  view,
  author,
  isAnonymous,
  variant,
  onAuthorClick,
  onBeneficiaryClick,
}) => {
  const styles = AVATAR_STYLES[variant];

  if (isAnonymous) {
    return (
      <UserAvatar
        imageUrl={undefined}
        fullName="Anonymous"
        className={styles.anonymous}
        size={styles.size}
        isAnonymous
        alt="Anonymous"
      />
    );
  }

  // Pending ask-on-behalf: stacked avatars (creator behind, beneficiary in front).
  if (view.kind === 'pending' && view.originalAsker && view.beneficiary) {
    return (
      <StackedAvatars
        creator={{
          imageUrl: view.originalAsker.profile_picture,
          fullName: view.originalAsker.full_name || 'Unknown User',
        }}
        beneficiary={{
          imageUrl: view.beneficiary.profile_picture,
          fullName: view.beneficiary.full_name || 'Unknown User',
        }}
        onCreatorClick={onAuthorClick}
        onBeneficiaryClick={onBeneficiaryClick}
      />
    );
  }

  const { user, onClick } = principalFor(view, author, onAuthorClick, onBeneficiaryClick);
  const fullName = user?.full_name || 'Unknown User';
  const isNavigable = isPathBuildable(user);

  const avatar = (
    <UserAvatar
      imageUrl={user?.profile_picture}
      fullName={fullName}
      className={isNavigable ? styles.principal : styles.principalStatic}
      size={styles.size}
      alt={fullName}
    />
  );

  // Degraded summary with neither id nor slug: no profile path can be built, so
  // render a static avatar with no button/click affordance (getProfilePath would
  // otherwise throw if the click handler ran).
  if (!isNavigable) return avatar;

  return (
    <button
      className={PRINCIPAL_BUTTON_CLASS}
      onClick={onClick}
      aria-label={`View ${fullName}'s profile`}
      type="button"
    >
      {avatar}
    </button>
  );
};

export const QuestionAuthorName: React.FC<QuestionAuthorBylineProps> = ({
  view,
  author,
  isAnonymous,
  variant,
  onAuthorClick,
  onBeneficiaryClick,
}) => {
  const styles = NAME_STYLES[variant];

  // Anonymity wins in every state, mirroring the `isAnonymous` short-circuit in
  // QuestionAuthorAvatar above. An anonymous question shows the static "Anonymous"
  // label even when asked on someone's behalf, so the creator's or beneficiary's
  // real name is never rendered beside an anonymized avatar. This is defense in
  // depth: the create path already blocks anonymous + on-behalf, but the
  // QuestionUpdate schema has no matching validator, so we also enforce the
  // invariant here at the render boundary.
  if (isAnonymous) {
    return <span className={cn(styles.base, styles.extra)}>Anonymous</span>;
  }

  const { user, onClick } = principalFor(view, author, onAuthorClick, onBeneficiaryClick);
  const fullName = user?.full_name || 'Unknown User';

  // Degraded summary with neither id nor slug: static text, no click affordance.
  if (!isPathBuildable(user)) {
    return <span className={cn(styles.base, styles.extra)}>{fullName}</span>;
  }

  return (
    <span className={cn(styles.base, NAME_INTERACTIVE, styles.extra)} onClick={onClick}>
      {fullName}
    </span>
  );
};
